import type { ReactElement, ReactNode } from "react";
import { ATTRIBUTES, type Rules } from "odyssey-shared";
import { Button, GlyphButton } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { backgroundName } from "../dialogue/marks.ts";
import { canAdd, pendingTotal } from "./allocation.ts";
import type { CharacterSnapshot, CharacterStore } from "./store.ts";

/**
 * The Character tab (ADR-138's amendment of 2026-10-31, decisions 2, 3 and 7): the header, the points line, the seven
 * attribute rows with + and -, the footer's note or refusal with Respec, Reset and Confirm. Test-after; everything it
 * shows comes from the store, which comes from the server.
 */

/**
 * Who the character is, for the header.
 */
export interface CharacterHeader {
  readonly name: string;
  readonly level: number;
}

/**
 * The header: a portrait box, the full name, the level and the background.
 *
 * @param props The header and the background id.
 * @returns The header.
 */
function Header({ header, background }: { readonly header: CharacterHeader; readonly background: string | null }): ReactElement {
  return (
    <div className="ot-character__header" data-character="header">
      <div className="ot-character__portrait" aria-hidden="true">
        <span className="ot-character__figure" />
      </div>
      <div className="ot-character__who">
        <div className="ot-character__name-row">
          <span className="ot-character__name">{header.name}</span>
          <span className="ot-character__level">{say("character.level", { level: String(header.level) })}</span>
        </div>
        <span className="ot-character__background">{background === null ? say("character.noBackground") : say("character.background", { background: backgroundName(background) })}</span>
      </div>
    </div>
  );
}

/**
 * The line above the rows: the points left of the points held while a choice is pending, the points to spend
 * otherwise, or that none are.
 *
 * @param props The points held and pending.
 * @returns The line.
 */
function PointsLine({ points, pending }: { readonly points: number; readonly pending: number }): ReactElement {
  const text = points === 0 ? say("character.noPoints") : pending > 0 ? say("character.pointsLeft", { left: String(points - pending), total: String(points) }) : points === 1 ? say("character.pointToSpend") : say("character.pointsToSpend", { count: String(points) });
  return (
    <div className={points === 0 ? "ot-character__points is-none" : "ot-character__points"} data-character="points" role="status">
      {text}
    </div>
  );
}

/**
 * The seven rows.
 *
 * @param props The snapshot, the store and the cap.
 * @returns The rows.
 */
function Rows({ snapshot, store, cap, phone }: { readonly snapshot: CharacterSnapshot; readonly store: CharacterStore; readonly cap: number; readonly phone: boolean }): ReactElement | null {
  const frame = snapshot.frame;
  if (frame === null) {
    return null;
  }
  return (
    <ul className="ot-character__rows" data-character="rows">
      {ATTRIBUTES.map((attribute, index) => {
        const pending = snapshot.pending[index] as number;
        const words = {
          short: say(`character.attributes.${attribute}.short`),
          name: say(`character.attributes.${attribute}.name`),
          effect: say(`character.attributes.${attribute}.effect`),
        };
        const addable = canAdd(frame, snapshot.pending, index, cap) && !snapshot.waiting;
        return (
          <li key={attribute} className="ot-character__row" data-attribute={attribute}>
            <span className="ot-character__short">{words.short}</span>
            <span className="ot-character__attr">{words.name}</span>
            {phone ? null : <span className="ot-character__effect">{words.effect}</span>}
            <span className="ot-character__minus">{pending > 0 ? <GlyphButton glyph="minus" size={phone ? 18 : 11} label={say("character.remove", { name: words.name })} onClick={() => store.remove(index)} disabled={snapshot.waiting} /> : null}</span>
            <span className={pending > 0 ? "ot-character__value is-pending" : "ot-character__value"} data-character="value">
              {(frame.effective[index] as number) + pending}
            </span>
            <span className="ot-character__plus">{frame.points > 0 ? <GlyphButton glyph="plus" size={phone ? 18 : 11} label={say("character.add", { name: words.name })} onClick={() => store.add(index)} disabled={!addable} /> : null}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The footer: the note or the refusal, the Respec button below level 10 (or the shrine line at and above it), Reset
 * and Confirm while a choice is pending.
 *
 * @param props The snapshot and the store.
 * @returns The footer.
 */
function Footer({ snapshot, store }: { readonly snapshot: CharacterSnapshot; readonly store: CharacterStore }): ReactElement {
  const pending = pendingTotal(snapshot.pending);
  const frame = snapshot.frame;
  const note = snapshot.refusal !== null ? say(`character.refused.${snapshot.refusal}`) : snapshot.waiting ? say("character.waiting") : pending > 0 ? say("character.unsaved") : frame?.respec === "shrine" ? say("character.respecShrine") : "";
  return (
    <div className="ot-character__footer" data-character="footer">
      <span className={snapshot.refusal !== null ? "ot-character__note is-refused" : "ot-character__note"} role="status">
        {note}
      </span>
      {pending > 0 ? (
        <>
          <Button onClick={() => store.reset()} disabled={snapshot.waiting}>
            {say("character.reset")}
          </Button>
          <Button variant="primary" onClick={() => store.ask("spend")} disabled={snapshot.waiting}>
            {say("character.confirm")}
          </Button>
        </>
      ) : frame?.respec === "free" ? (
        <Button onClick={() => store.ask("respec")} disabled={snapshot.waiting}>
          {say("character.respec")}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * The Character tab's page.
 *
 * @param props The snapshot, the store, the rules, the header, whether the phone form is drawn and, in it, what the
 *   header's pane holds under the header (the preview panel).
 * @returns The page.
 */
export function CharacterPage({ snapshot, store, rules, header, phone, aside }: { readonly snapshot: CharacterSnapshot; readonly store: CharacterStore; readonly rules: Rules; readonly header: CharacterHeader; readonly phone: boolean; readonly aside?: ReactNode }): ReactElement {
  const pending = pendingTotal(snapshot.pending);
  const head = <Header header={header} background={snapshot.frame?.background ?? null} />;
  const body = (
    <>
      <PointsLine points={snapshot.frame?.points ?? 0} pending={pending} />
      <Rows snapshot={snapshot} store={store} cap={rules.constants.attribute_cap} phone={phone} />
      <Footer snapshot={snapshot} store={store} />
    </>
  );
  if (phone) {
    return (
      <div className="ot-character__page ot-character__page--phone" data-character="page">
        <div className="ot-character__pane">
          {head}
          {aside}
        </div>
        <div className="ot-character__main">{body}</div>
      </div>
    );
  }
  return (
    <div className="ot-character__page" data-character="page">
      {head}
      {body}
    </div>
  );
}
